/* =============================================================
 * cart-events-bridge.js
 * -------------------------------------------------------------
 * Single source of truth for cart mutations + cross-app event
 * compatibility. Designed to make this AJAX theme play nicely
 * with auto-gift apps like Quikify, BOGOS, Salepify, Free Gifts
 * by Secomapp, etc.
 *
 * Public API:
 *   window.themeCart.add(items, opts)
 *   window.themeCart.change({ id|line, quantity, properties })
 *   window.themeCart.update({ updates, attributes, note })
 *   window.themeCart.clear()
 *   window.themeCart.refresh()        // re-fetch /cart.js + re-render
 *   window.themeCart.getCart()        // last cached cart object
 *
 * Outbound events (we dispatch — apps listen):
 *   - cart:updated          {detail: {cart, source}}
 *   - cart:refresh          (legacy alias used by BOGOS)
 *   - cart.requestComplete  (legacy alias)
 *
 * Inbound events (apps dispatch — we listen and re-render):
 *   - cart:refresh
 *   - bogos:variant-added
 *   - bogos:variant-removed
 *   - quikify:cart-changed
 *   - secomapp:gift-added
 *   - cart:item-added (generic)
 *
 * Render hook:
 *   Provide a function on window.themeCart.render(cart, sections)
 *   from your AJAX-cart code. The bridge calls it whenever the
 *   cart needs to repaint. If you need server-rendered HTML for
 *   sections (cart drawer, line items, mini-cart icon), pass
 *   their section IDs in DEFAULT_SECTIONS below.
 * ============================================================= */

(function () {
  'use strict';

  /* -----------------------------------------------------------
   * 1. CONFIG — section list is built dynamically at request time
   * because the cart page uses JSON-template dynamic section IDs
   * (e.g. "cart-items" instead of "main-cart-items").
   * --------------------------------------------------------- */
  function getSectionsForRefresh() {
    var ids = ['cart-drawer', 'cart-icon-bubble'];
    var cartItems = document.getElementById('main-cart-items');
    var cartFooter = document.getElementById('main-cart-footer');
    if (cartItems && cartItems.dataset && cartItems.dataset.id) ids.push(cartItems.dataset.id);
    if (cartFooter && cartFooter.dataset && cartFooter.dataset.id) ids.push(cartFooter.dataset.id);
    return ids;
  }

  // Inbound events from third-party apps we want to react to.
  var INBOUND_EVENTS = [
    'cart:refresh',
    'bogos:variant-added',
    'bogos:variant-removed',
    'quikify:cart-changed',
    'secomapp:gift-added',
    'cart:item-added'
  ];

  /* -----------------------------------------------------------
   * 2. STATE
   * --------------------------------------------------------- */
  var lastCart = null;        // cached last-known cart object
  var inFlight = null;        // promise of current in-flight request (serialise)
  var refreshPending = false; // debounce flag for refresh-only calls

  /* -----------------------------------------------------------
   * 3. LOW-LEVEL FETCH HELPERS
   * --------------------------------------------------------- */
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=utf-8',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body || {})
    }).then(handleResponse);
  }

  function getJSON(url) {
    return fetch(url, {
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    }).then(handleResponse);
  }

  function handleResponse(res) {
    return res.json().then(function (data) {
      if (!res.ok) {
        var err = new Error(data && data.description ? data.description : 'Cart request failed');
        err.status = res.status;
        err.body = data;
        throw err;
      }
      return data;
    });
  }

  /* -----------------------------------------------------------
   * 4. CANONICAL REFRESH
   * Fetches /cart.js and (optionally) Sections API HTML.
   * Always called AFTER any mutation so we capture gifts that
   * apps injected during the same tick.
   * --------------------------------------------------------- */
  function refreshCart(opts) {
    opts = opts || {};
    var sections = opts.sections === false ? null : (opts.sections || getSectionsForRefresh());

    var url = '/cart.js';
    if (sections && sections.length) {
      // The Sections API attaches { sections: { id: html, ... } } to
      // the cart payload. Bundling it in /cart.js avoids a second RTT.
      url += '?sections=' + encodeURIComponent(sections.join(',')) +
             '&sections_url=' + encodeURIComponent(window.location.pathname);
    }
    return getJSON(url).then(function (cart) {
      lastCart = cart;
      // Hand off to the theme's render layer.
      var sectionsHtml = cart.sections || null;
      if (typeof window.themeCart.render === 'function') {
        try { window.themeCart.render(cart, sectionsHtml); }
        catch (e) { console.error('[cart-bridge] render error', e); }
      }
      broadcast(cart, opts.source || 'refresh');
      return cart;
    });
  }

  /* -----------------------------------------------------------
   * 5. OUTBOUND EVENT BROADCAST
   * Several apps listen to different event names. Firing all
   * of the common ones is cheap insurance against breakage when
   * a customer installs a new app later.
   * --------------------------------------------------------- */
  function broadcast(cart, source) {
    // The "__bridge: true" marker lets our own inbound listeners
    // skip the echo. Without it, every outbound cart:refresh would
    // re-trigger the inbound listener and cause an infinite refresh
    // loop (61, 124, ... repeats in the console).
    var detail = { cart: cart, source: source || 'theme', __bridge: true };

    // Modern, recommended canonical event.
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: detail, bubbles: true }));

    // Legacy / aliased names that several auto-gift apps still subscribe to.
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: detail, bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart.requestComplete', { detail: detail, bubbles: true }));

    // Shopify Section Rendering theme convention (Dawn-style PubSub).
    // Fire only if Dawn's PUB_SUB_EVENTS layer is loaded.
    if (window.PUB_SUB_EVENTS && window.publish) {
      try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, detail); } catch (e) {}
    }

    // Liquid Ajax Cart and the older Shopify.onItemAdded callbacks.
    if (window.Shopify && typeof window.Shopify.onItemAdded === 'function') {
      try { window.Shopify.onItemAdded(cart); } catch (e) {}
    }
  }

  /* -----------------------------------------------------------
   * 6. PUBLIC MUTATION METHODS
   * Each one performs the request, then delegates to refreshCart()
   * so the next render reflects whatever the app silently added.
   * Requests are serialised (chained) to avoid races: an app's
   * gift-add fired in parallel with our quantity-change would
   * otherwise leave the UI showing a stale subtotal.
   * --------------------------------------------------------- */
  function serialise(taskFn) {
    var run = function () {
      return taskFn().catch(function (err) {
        console.error('[cart-bridge] mutation failed', err);
        // Refresh anyway so UI doesn't drift from server state.
        return refreshCart({ source: 'error-recovery' }).then(function () { throw err; });
      });
    };
    inFlight = inFlight ? inFlight.then(run, run) : run();
    return inFlight;
  }

  function add(items, opts) {
    // items: single object or array. /cart/add.js accepts either.
    var body = Array.isArray(items) ? { items: items } : items;
    return serialise(function () {
      return postJSON('/cart/add.js', body).then(function () {
        return refreshCart({ source: (opts && opts.source) || 'add' });
      });
    });
  }

  function change(payload) {
    return serialise(function () {
      return postJSON('/cart/change.js', payload).then(function () {
        return refreshCart({ source: 'change' });
      });
    });
  }

  function update(payload) {
    return serialise(function () {
      return postJSON('/cart/update.js', payload).then(function () {
        return refreshCart({ source: 'update' });
      });
    });
  }

  function clear() {
    return serialise(function () {
      return postJSON('/cart/clear.js', {}).then(function () {
        return refreshCart({ source: 'clear' });
      });
    });
  }

  /* -----------------------------------------------------------
   * 7. INBOUND LISTENERS
   * When BOGOS / Quikify / etc. inject or remove a gift via
   * their own /cart/add.js call, they then dispatch one of the
   * known events. We re-fetch and re-render. Debounced so a
   * burst of events (gift-added then cart-refresh) only causes
   * one repaint.
   * --------------------------------------------------------- */
  function scheduleInboundRefresh(source) {
    if (refreshPending) return;
    refreshPending = true;
    // Microtask delay coalesces synchronous bursts; a 50ms timer
    // catches near-simultaneous events from chained app callbacks.
    setTimeout(function () {
      refreshPending = false;
      refreshCart({ source: 'inbound:' + source }).catch(function () {});
    }, 50);
  }

  INBOUND_EVENTS.forEach(function (name) {
    document.addEventListener(name, function (e) {
      // Guard against echo loops: ignore events we just emitted.
      if (e && e.detail && e.detail.__bridge === true) return;
      scheduleInboundRefresh(name);
    });
  });

  /* -----------------------------------------------------------
   * 8. NETWORK-LEVEL SAFETY NET
   * Some older apps don't dispatch any event — they just call
   * /cart/add.js directly. Patch fetch + XMLHttpRequest so we
   * still detect and re-render after their request completes.
   * Only triggers when the call did NOT originate from us.
   * --------------------------------------------------------- */
  var CART_MUTATION_RX = /\/cart\/(add|change|update|clear)\.js/;

  // -- fetch patch
  var origFetch = window.fetch && window.fetch.bind(window);
  if (origFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = origFetch(input, init);
      if (CART_MUTATION_RX.test(url)) {
        promise.then(function (res) {
          if (!res || !res.ok) return;
          // Skip if we initiated this — our own postJSON sets X-Requested-With,
          // and refreshCart() already runs after our mutations.
          var headers = init && init.headers;
          var ours = headers && (
            (headers['X-Requested-With'] === 'XMLHttpRequest') ||
            (typeof headers.get === 'function' && headers.get('X-Requested-With') === 'XMLHttpRequest')
          );
          if (ours) return;
          scheduleInboundRefresh('fetch:' + url);
        }).catch(function () {});
      }
      return promise;
    };
  }

  // -- XHR patch (BOGOS, older apps)
  var OrigXHR = window.XMLHttpRequest;
  if (OrigXHR) {
    var origOpen = OrigXHR.prototype.open;
    var origSend = OrigXHR.prototype.send;
    OrigXHR.prototype.open = function (method, url) {
      this.__cartUrl = url;
      return origOpen.apply(this, arguments);
    };
    OrigXHR.prototype.send = function () {
      var self = this;
      if (CART_MUTATION_RX.test(self.__cartUrl || '')) {
        self.addEventListener('load', function () {
          if (self.status >= 200 && self.status < 300) {
            scheduleInboundRefresh('xhr:' + self.__cartUrl);
          }
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  /* -----------------------------------------------------------
   * 9. EXPORT
   * --------------------------------------------------------- */
  window.themeCart = window.themeCart || {};
  window.themeCart.add = add;
  window.themeCart.change = change;
  window.themeCart.update = update;
  window.themeCart.clear = clear;
  window.themeCart.refresh = refreshCart;
  window.themeCart.getCart = function () { return lastCart; };
  /* -----------------------------------------------------------
   * Default render. Repaints standard targets in this theme:
   *   - #CartDrawer  (drawer body)
   *   - #cart-icon-bubble  (header counter)
   *   - #main-cart-items .js-contents  (cart page line items)
   *   - #main-cart-footer .js-contents  (cart page totals)
   * Override by reassigning window.themeCart.render after this
   * script loads if you need different behavior.
   * --------------------------------------------------------- */
  function parseInner(html, selector) {
    if (!html) return null;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  }

  function defaultRender(cart, sections) {
    if (!sections) return;

    // 1. Drawer
    var drawerBody = document.getElementById('CartDrawer');
    if (drawerBody && sections['cart-drawer']) {
      var drawerInner = parseInner(sections['cart-drawer'], '#CartDrawer');
      if (drawerInner !== null) drawerBody.innerHTML = drawerInner;
    }

    // 2. Header cart icon
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      var bubbleInner = parseInner(sections['cart-icon-bubble'], '.shopify-section');
      if (bubbleInner !== null) bubble.innerHTML = bubbleInner;
    }

    // 3. Cart page (only present when /cart is the current route)
    [['main-cart-items'], ['main-cart-footer']].forEach(function (entry) {
      var domId = entry[0];
      var el = document.getElementById(domId);
      if (!el) return;
      var sectionKey = el.dataset && el.dataset.id;
      if (!sectionKey || !sections[sectionKey]) return;
      var inner = parseInner(sections[sectionKey], '.js-contents') ||
                  parseInner(sections[sectionKey], '.shopify-section');
      if (inner === null) return;
      var jsContents = el.querySelector('.js-contents');
      (jsContents || el).innerHTML = inner;
    });

    // 4. Empty-state classes — used by Dawn to hide totals/actions when 0 items.
    var emptyTargets = document.querySelectorAll(
      'cart-drawer, .cart, #main-cart-items, #main-cart-footer'
    );
    emptyTargets.forEach(function (el) {
      el.classList.toggle('is-empty', cart.item_count === 0);
    });
  }

  if (typeof window.themeCart.render !== 'function') {
    window.themeCart.render = defaultRender;
  }

  // Initial cache so callers can read window.themeCart.getCart() immediately.
  refreshCart({ source: 'init', sections: false }).catch(function () {});
})();
